import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Stack
} from '@mui/material';
import {
  CloudUpload,
  CloudDownload,
  CheckCircle,
  Error,
  Warning,
  ExpandMore,
  ExpandLess,
  Refresh
} from '@mui/icons-material';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { useLanguage } from '../../contexts/LanguageContext';

interface ImportResult {
  totalRecords: number;
  successCount: number;
  updatedCount: number;
  failureCount: number;
  errors: string[];
  updatedAccounts: string[];
  importedAccounts: any[];
  summary: string;
}

// ═══════════════════════════════════════════════════════════════
// 🌍 الترجمات
// ═══════════════════════════════════════════════════════════════
const translations = {
  en: {
    pageTitle: 'Chart of Accounts - Import/Export',
    exportTitle: '📤 Export to Excel',
    exportDescription: 'Download all accounts in a formatted Excel file with hierarchical colors',
    exportButton: 'Export Chart of Accounts',
    importTitle: '📥 Import from Excel',
    importDescription: 'Upload an Excel file to import or update accounts. Existing accounts will be updated automatically.',
    chooseFile: 'Choose Excel File',
    selectedFile: 'Selected',
    importButton: 'Import Accounts',
    selectFileFirst: 'Please select an Excel file first',
    exportFailed: 'Export failed',
    importFailed: 'Import failed',
    unknownError: 'Unknown error',
    
    // Summary
    summaryTitle: '📊 Import Summary',
    total: 'Total',
    added: 'Added',
    updated: 'Updated',
    failed: 'Failed',
    
    // Imported Accounts
    successfullyImported: '✅ Successfully Imported Accounts',
    accountCode: 'Account Code',
    accountName: 'Account Name',
    accountNameAr: 'Account Name (AR)',
    type: 'Type',
    level: 'Level',
    
    // Updated Accounts
    updatedAccountsTitle: '🔄 Updated Accounts',
    updatedMessage: 'These accounts were updated with new data from Excel',
    
    // Errors
    importErrorsTitle: '❌ Import Errors',
  },
  ar: {
    pageTitle: 'دليل الحسابات - استيراد/تصدير',
    exportTitle: '📤 تصدير إلى Excel',
    exportDescription: 'تحميل جميع الحسابات في ملف Excel منسق مع ألوان هرمية',
    exportButton: 'تصدير دليل الحسابات',
    importTitle: '📥 استيراد من Excel',
    importDescription: 'رفع ملف Excel لاستيراد أو تحديث الحسابات. سيتم تحديث الحسابات الموجودة تلقائياً.',
    chooseFile: 'اختر ملف Excel',
    selectedFile: 'الملف المحدد',
    importButton: 'استيراد الحسابات',
    selectFileFirst: 'الرجاء اختيار ملف Excel أولاً',
    exportFailed: 'فشل التصدير',
    importFailed: 'فشل الاستيراد',
    unknownError: 'خطأ غير معروف',
    
    // Summary
    summaryTitle: '📊 ملخص الاستيراد',
    total: 'الإجمالي',
    added: 'المُضاف',
    updated: 'المُحدّث',
    failed: 'الفاشل',
    
    // Imported Accounts
    successfullyImported: '✅ الحسابات المُستوردة بنجاح',
    accountCode: 'رمز الحساب',
    accountName: 'اسم الحساب',
    accountNameAr: 'اسم الحساب (عربي)',
    type: 'النوع',
    level: 'المستوى',
    
    // Updated Accounts
    updatedAccountsTitle: '🔄 الحسابات المُحدّثة',
    updatedMessage: 'تم تحديث هذه الحسابات ببيانات جديدة من Excel',
    
    // Errors
    importErrorsTitle: '❌ أخطاء الاستيراد',
  }
};

const ChartOfAccountsImportExportPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language as 'en' | 'ar'];
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showImported, setShowImported] = useState(true);
  const [showUpdated, setShowUpdated] = useState(true);
  const [showErrors, setShowErrors] = useState(true);

  // ═══════════════════════════════════════════════════════════════
  // 📤 تصدير Excel
  // ═══════════════════════════════════════════════════════════════
  const handleExport = async () => {
    try {
      setLoading(true);
      await chartOfAccountsService.exportToExcel();
    } catch (error: any) {
      alert(`${t.exportFailed}: ${error.message || t.unknownError}`);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // 📥 اختيار ملف Excel
  // ═══════════════════════════════════════════════════════════════
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // 📥 استيراد Excel
  // ═══════════════════════════════════════════════════════════════
  const handleImport = async () => {
    if (!selectedFile) {
      alert(t.selectFileFirst);
      return;
    }

    try {
      setLoading(true);
      const result = await chartOfAccountsService.importFromExcel(selectedFile);
      setImportResult(result);
      setSelectedFile(null);
      
      // إعادة تعيين input
      const fileInput = document.getElementById('excel-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      alert(`${t.importFailed}: ${error.response?.data?.message || error.message || t.unknownError}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 🎯 العنوان */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t.pageTitle}
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 🎛️ أزرار Export/Import */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              alignItems: 'stretch'
            }}
          >
            {/* تصدير */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6">
                {t.exportTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.exportDescription}
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<CloudDownload />}
                onClick={handleExport}
                disabled={loading}
                fullWidth
              >
                {t.exportButton}
              </Button>
            </Box>

            {/* فاصل */}
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ display: { xs: 'none', md: 'block' } }} 
            />
            <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

            {/* استيراد */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6">
                {t.importTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t.importDescription}
              </Typography>
              
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                disabled={loading}
              >
                {t.chooseFile}
                <input
                  id="excel-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFile && (
                <Alert severity="info">
                  {t.selectedFile}: <strong>{selectedFile.name}</strong>
                </Alert>
              )}

              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUpload />}
                onClick={handleImport}
                disabled={loading || !selectedFile}
                fullWidth
              >
                {t.importButton}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 📊 نتائج الاستيراد */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {importResult && (
        <>
          {/* الإحصائيات */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t.summaryTitle}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
                <Chip 
                  label={`${t.total}: ${importResult.totalRecords}`} 
                  color="default" 
                  size="medium"
                />
                <Chip 
                  icon={<CheckCircle />}
                  label={`${t.added}: ${importResult.successCount}`} 
                  color="success" 
                  size="medium"
                />
                <Chip 
                  icon={<Refresh />}
                  label={`${t.updated}: ${importResult.updatedCount}`} 
                  color="info" 
                  size="medium"
                />
                <Chip 
                  icon={<Error />}
                  label={`${t.failed}: ${importResult.failureCount}`} 
                  color="error" 
                  size="medium"
                />
              </Stack>
              <Alert severity={importResult.successCount > 0 || importResult.updatedCount > 0 ? 'success' : 'info'}>
                {importResult.summary}
              </Alert>
            </CardContent>
          </Card>

          {/* الحسابات المُضافة بنجاح */}
          {importResult.successCount > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="success.main">
                    {t.successfullyImported} ({importResult.successCount})
                  </Typography>
                  <IconButton onClick={() => setShowImported(!showImported)}>
                    {showImported ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={showImported}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>{t.accountCode}</strong></TableCell>
                          <TableCell><strong>{t.accountName}</strong></TableCell>
                          <TableCell><strong>{t.accountNameAr}</strong></TableCell>
                          <TableCell><strong>{t.type}</strong></TableCell>
                          <TableCell><strong>{t.level}</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResult.importedAccounts.map((account, index) => (
                          <TableRow key={index}>
                            <TableCell>{account.accountCode}</TableCell>
                            <TableCell>{account.accountName}</TableCell>
                            <TableCell>{account.accountNameAr}</TableCell>
                            <TableCell>{account.accountType}</TableCell>
                            <TableCell>{account.level}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* الحسابات المُحدّثة */}
          {importResult.updatedCount > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="info.main">
                    {t.updatedAccountsTitle} ({importResult.updatedCount})
                  </Typography>
                  <IconButton onClick={() => setShowUpdated(!showUpdated)}>
                    {showUpdated ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={showUpdated}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t.updatedMessage}
                  </Alert>
                  <Stack spacing={1}>
                    {importResult.updatedAccounts.map((msg, index) => (
                      <Alert key={index} severity="info" variant="outlined">
                        {msg}
                      </Alert>
                    ))}
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* الأخطاء */}
          {importResult.failureCount > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="error.main">
                    {t.importErrorsTitle} ({importResult.failureCount})
                  </Typography>
                  <IconButton onClick={() => setShowErrors(!showErrors)}>
                    {showErrors ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={showErrors}>
                  <Stack spacing={1}>
                    {importResult.errors.map((error, index) => (
                      <Alert key={index} severity="error" variant="outlined">
                        {error}
                      </Alert>
                    ))}
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default ChartOfAccountsImportExportPage;